import { useState, useEffect, useCallback } from 'react';
import { fetchCustomers, searchCustomers } from '../api/customersApi';
import type { CustomerListItem } from '../types';

interface UseCustomersOptions {
  businessId: string | null;
  autoFetch?: boolean;
}

interface UseCustomersReturn {
  customers: CustomerListItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  search: (term: string) => Promise<void>;
  clearSearch: () => void;
  isSearching: boolean;
}

/**
 * Hook to fetch and search customers for a business
 */
export function useCustomers({
  businessId,
  autoFetch = true,
}: UseCustomersOptions): UseCustomersReturn {
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [allCustomers, setAllCustomers] = useState<CustomerListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCustomers = useCallback(async () => {
    if (!businessId) {
      setCustomers([]);
      setAllCustomers([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchCustomers(businessId);
      setCustomers(data);
      setAllCustomers(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load customers';
      setError(message);
      console.error('[useCustomers] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  const search = useCallback(
    async (term: string) => {
      if (!businessId) return;

      if (!term.trim()) {
        setCustomers(allCustomers);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const data = await searchCustomers(businessId, term);
        setCustomers(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to search customers';
        setError(message);
        console.error('[useCustomers] Search error:', err);
      } finally {
        setIsSearching(false);
      }
    },
    [businessId, allCustomers]
  );

  const clearSearch = useCallback(() => {
    setCustomers(allCustomers);
  }, [allCustomers]);

  useEffect(() => {
    if (autoFetch && businessId) {
      loadCustomers();
    }
  }, [autoFetch, businessId, loadCustomers]);

  return {
    customers,
    isLoading,
    error,
    refetch: loadCustomers,
    search,
    clearSearch,
    isSearching,
  };
}